<template>
  <div
    v-if="showBanner"
    class="gdpr-banner"
  >
    <b-row>
      <b-col
        cols="12"
        class="text-left"
      >
        <p>
          {{$t('gdpr.about_cookies_using')}}
        </p>
      </b-col>

      <b-col>
        <app-collapse class="mb-1 mb-md-0">
          <app-collapse-item
            :title="$t('gdpr.options')"
            :type="collapseType"
            :is-visible="true"
          >
            <template #header>
                <div class="text-uppercase font-medium-1 font-weight-bolder">{{$t('gdpr.options') }}</div>
            </template>
            <div class="text-left my-1">
              {{$t('gdpr.decide_which_cookies_activated')}}
              <i18n path="message.see_for_more_details">
                <template #link>
                  <b-link :to="`/${$i18n.locale}/privacy-policy`">
                    {{ $t('Privacy policy') }}
                  </b-link>
                </template>
              </i18n>.
            </div>
            <b-form-checkbox-group
              v-model="consent"
              class="d-flex justify-content-around"
            >
              <b-row>
                <b-col
                  v-for="item in options"
                  :key="item.value"
                  cols="12"
                  md="6"
                >
                  <b-form-checkbox
                    :value="item.value"
                    :disabled="item.disabled"
                    name="cookie-category"
                  >
                    <div class="text-left">
                      <span class="font-weight-bold">{{ $t(item.name) }}</span>
                    </div>
                  </b-form-checkbox>
                  <p>{{ $t(item.desc) }}</p>
                </b-col>
              </b-row>
            </b-form-checkbox-group>
          </app-collapse-item>
        </app-collapse>
      </b-col>

      <b-col
        cols="12"
        class="text-center"
      >
        <b-button
          v-if="consent.length === 1 && consent.includes('NECESSARY')"
          variant="outline-primary"
          class="text-uppercase"
          @click="declineConsent"
        >
          {{ $t('Decline') }}
        </b-button>
        <b-button
          v-else
          variant="outline-primary"
          class="text-uppercase"
          @click="acceptOnlySelected"
        >
          {{ $t('Accept only selected') }}
        </b-button>
        <b-button
          variant="primary"
          class="text-uppercase"
          @click="acceptAll"
        >
          {{ $t('Accept') }}
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { ref, onMounted, computed,getCurrentInstance } from '@vue/composition-api'
import {
  BLink,
  BButton,
  BFormCheckboxGroup,
  BFormCheckbox,
  BRow,
  BCol,
} from 'bootstrap-vue'
import { initAnalytics } from '@/plugins/vue-analytics'
import { setCookie, getCookie } from '@/utils/cookie'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'

export default {
  name: 'GdprConsent',
  components: {
    BLink,
    BButton,
    BRow,
    BCol,
    BFormCheckboxGroup,
    BFormCheckbox,
    AppCollapse,
    AppCollapseItem,
  },
  setup() {
    const consentGiven = ref(false)
    const collapseType = 'default'
    const consent = ref(['NECESSARY'])
    const gaPrefixes = ['_ga', '_gid', '_gat', '_ga_*']
    const options = [
      {
        value: 'NECESSARY',
        name: 'gdpr.necessary',
        disabled: true,
        desc: 'gdpr.about_necessary_cookies',
      },
      {
        value: 'PERFORMANCE',
        name: 'gdpr.performance',
        desc: 'gdpr.about_performance_cookies',
      },
      {
        value: 'FUNCTIONAL',
        name: 'gdpr.functional',
        desc: 'gdpr.about_functional_cookies',
      },
      {
        value: 'MARKETING',
        name: 'gdpr.marketing',
        desc: 'gdpr.about_marketing_cookies',
      },
    ]

    const acceptAll = () => {
      const allValues = options.map(opt => opt.value)
      setCookie('gdpr_consent', JSON.stringify(allValues), 365)
      consent.value = allValues
      consentGiven.value = true

      initAnalytics()
    }

    const acceptOnlySelected = () => {
      setCookie('gdpr_consent', JSON.stringify(consent.value), 365)
      consentGiven.value = true

      if (consent.value.includes('MARKETING')) {
        initAnalytics()
      }
    }

    const declineConsent = () => {
      setCookie('gdpr_consent', 'DECLINED', 365)
      consentGiven.value = true
      waitForGACookies(deleteGaCookies)
    }

    const deleteGaCookies = () => {
      window['gtag_disable_' + process.env.VUE_APP_GOOGLE_ANALYTICS_KEY] = true
      const allCookies = document.cookie.split(';')
      allCookies.forEach(cookie => {
        const name = cookie.split('=')[0].trim()
        if (gaPrefixes.some(prefix => name.startsWith(prefix))) {
          document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`
          document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}`
        }
      })

      // Optional: remove from localStorage
      localStorage.removeItem('_ga')
    }

    const waitForGACookies = (callback, timeout = 3000)=> {
      const start = Date.now()

      function check() {
        const cookies = document.cookie
        if (cookies.includes('_ga') || cookies.includes('_gid')) {
          callback()
        } else if (Date.now() - start < timeout) {
          setTimeout(check, 300)
        } else {
          callback() // Fallback: run anyway after timeout
        }
      }

      check()
    }

    onMounted(() => {
      const storedConsent = getCookie('gdpr_consent')
      if (!storedConsent) return
      consentGiven.value = true
      if (consent.value.includes('MARKETING')) {
        initAnalytics()
      } else {
        waitForGACookies(deleteGaCookies)
      }
    })
    // Get current route reactively
    const vm = getCurrentInstance().proxy

    const showBanner = computed(() => {
      const routeName = vm.$route?.name || ''
      return !consentGiven.value && !routeName.includes('privacy-policy')
    })

    return {
      consentGiven,
      collapseType,
      consent,
      options,
      acceptAll,
      acceptOnlySelected,
      declineConsent,
      showBanner,
    }
  },
}
</script>

<style lang="scss" scoped>
.gdpr-banner {
  position: fixed;
  bottom: 0;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  background: #f7f7f7;
  border-top: 1px solid #ccc;
  padding: 20px;
  z-index: 1032;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.05);
}

@media (max-width: 768px) {
  .gdpr-banner {
    padding: 16px;
    max-height: 80vh;
  }
}

button {
  margin: 0.25rem;
}

.collapse-default .card {
  margin: 0;
  box-shadow: none !important;
  background-color: transparent;
}
</style>
